import { type PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react';
import type { SearchResults } from 'algoliasearch-helper';
import type {
  CurrentRefinementsConnectorParamsRefinement,
  CurrentRefinementsRenderState,
} from 'instantsearch.js/es/connectors/current-refinements/connectCurrentRefinements';
import type { UiState } from 'instantsearch.js';
import { useCurrentRefinements, useInstantSearch } from 'react-instantsearch';

import { Panel } from './Panel';
import { useCloseDropdown } from './hooks/useCloseDropdown';
import { useLockedBody } from './hooks/useLockedBody';
import { useMediaQuery } from './hooks/useMediaQuery';
import cn from 'clsx';

import { capitalize, getFirstChildPropValue } from '@/lib/utils';
import { Button } from '@/components/ui/button';

export type DropdownProps = PropsWithChildren<{
  buttonText?: string | ((options: DropdownButtonTextOptions) => string);
  classNames?: Partial<DropdownClassNames>;
  closeOnChange?: boolean | (() => boolean);
}>;

export type DropdownButtonTextOptions = {
  results: SearchResults;
  uiState: UiState;
  refinements: CurrentRefinementsConnectorParamsRefinement[];
};

export type DropdownClassNames = {
  root: string;
  button: string;
  buttonRefined: string;
  closeButton: string;
  mobileTitle: string;
};

type MiddlewareProps = Pick<DropdownProps, 'closeOnChange'> & {
  isOpened: boolean;
  close: () => void;
};

function getAttributeRefinements(attribute: string, items: CurrentRefinementsRenderState['items']) {
  const item = items.find(item => item.attribute === attribute);
  return item?.refinements ?? [];
}

function DropdownMiddleware({ isOpened, closeOnChange, close }: MiddlewareProps) {
  const { addMiddlewares } = useInstantSearch();

  useEffect(() =>
    addMiddlewares(() => ({
      onStateChange() {
        const shouldCloseOnChange =
          closeOnChange === true || (typeof closeOnChange === 'function' && closeOnChange() === true);

        // Close the dropdown if it's opened and `closeOnChange` is true
        if (isOpened && shouldCloseOnChange) {
          close();
        }
      },
    })),
  );

  return null;
}

export function FacetDropdown({ children, buttonText, closeOnChange, classNames = {} }: DropdownProps) {
  const { results, uiState } = useInstantSearch();
  const { items } = useCurrentRefinements({}, { $$widgetType: 'cmty.facetDropdown' });
  const [isOpened, setIsOpened] = useState(false);
  const panelRef = useRef(null);

  // Close the dropdown when click outside or press the Escape key
  useCloseDropdown(panelRef, () => setIsOpened(false), isOpened);

  // Prevent scrolling on mobile when the dropdown is opened
  const isMobile = useMediaQuery('(max-width: 375px)');
  useLockedBody(isOpened && isMobile);

  // Get the attribute(s) of the first child widget
  const attributeProp = getFirstChildPropValue(children, (props: unknown) => {
    if (props && typeof props === 'object' && 'attributes' in props) {
      return 'attributes';
    }
    return 'attribute';
  });

  if (!attributeProp) {
    throw new Error('<Dropdown> widget only supports InstantSearch widgets with an `attribute` or `attributes` prop.');
  }
  // Get the refinements for the attribute
  const attribute = typeof attributeProp === 'string' ? attributeProp : attributeProp[0];
  const refinements = getAttributeRefinements(attribute ?? '', items);
  const isRefined = refinements.length > 0;
  const isDisabled = results.hits.length === 0;

  // Get the header button text
  let text;
  if (typeof buttonText === 'string') {
    text = buttonText;
  } else if (typeof buttonText === 'function') {
    text = buttonText({ results, uiState, refinements });
  } else if (typeof attribute === 'string') {
    text = isRefined ? `${capitalize(attribute)} (${refinements.length})` : capitalize(attribute);
  }

  const header = (
    <Button
      variant={'outline'}
      type="button"
      className={cn(
        'font-sans text-[12px] font-medium tracking-tight text-gray-900',
        isRefined && cn('ais-Dropdown-button--refined', classNames.buttonRefined),
        isDisabled && 'ais-Dropdown-button--disabled',
      )}
      disabled={isDisabled}
      onClick={() => setIsOpened(opened => !opened)}>
      {text}
    </Button>
  );

  const footer = (
    <button className={cn('ais-Dropdown-close ais-Dropdown-button', classNames.closeButton)} onClick={close}>
      Apply
    </button>
  );

  return (
    <Panel
      header={header}
      footer={footer}
      className={cn('ais-Dropdown', isOpened && 'ais-Dropdown--opened z-20', classNames.root)}
      ref={panelRef}>
      <DropdownMiddleware isOpened={isOpened} closeOnChange={closeOnChange} close={close} />
      <h2 className={cn('ais-Dropdown-mobileTitle', classNames.mobileTitle)}>{text}</h2>
      {children}
    </Panel>
  );
}
